import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Download, Eye } from "lucide-react";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAreaList, useExportConsumerList, useSubAreaList } from "@features/bx/bill-cycle/hooks";
import { useChoices, useConsumerList } from "@features/bx/bill-cycle-v2/hooks";
import { TableColumn } from "@shared/types/table";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";

interface Consumer {
  id: string;
  name: string;
  accountNumber: string;
  premiseId: string;
  area: string;
  subarea: string;
  lastBilledDate: string;
  billedAmount: string;
  status: "ACTIVE" | "PAUSE" | "DISCONNECTED";
  lastBillType: "Actual" | "Estimated";
}

interface ConsumerlistProps {
}

export const BillCycleConsumerList: React.FC<ConsumerlistProps> = ({
}) => {

  const [selectedConsumers, setSelectedConsumers] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [selectedSubAreas, setSelectedSubAreas] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [isExport, setIsExport] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [consumerQueryParams, setConsumerQueryParams] = useState({
    page: currentPage,
    status: "",
    billType: "",
    area: [],
    sub_area: [],
    export: false,
    searchData: search,
  });

   const queryParams = useMemo(
  () => ({
    ...consumerQueryParams,
    page: currentPage,
  }),
  [consumerQueryParams, currentPage]
);

  const params = useParams();
  const id = params?.id;
  const action = "";
  const navigate = useNavigate();

  const { data: consumerList } = useConsumerList(queryParams, id);
  const { data: areaList } = useAreaList();
  const {data: statusChoices } = useChoices("bill_status")
  const { data: billTypeChoices} = useChoices('bill_type');

  const statusOptions = mapToLabelValue(statusChoices);
  const billTypeOptions = mapToLabelValue(billTypeChoices);
  // // Only fetch subAreaList when selectedAreas has values
  const { data: subAreaList = [] } = useSubAreaList(
    selectedAreas.length > 0 ? selectedAreas : []
  );

  // Memoize subArea options to prevent unnecessary recalculations
  const subAreaListConfig = useMemo(() => {
    return subAreaList.map((subCategory: any) => ({
      value: subCategory.id,
      label: subCategory.name,
    }));
  }, [subAreaList]);

  const filterFields: FormField[] = useMemo(() => [
    {
      name: "status",
      label: "Status",
      type: "select",
      placeholder: "All Status",
      required: false,
      options: [
        {
          label: 'Paid',
          value: "paid",
        },
        {
          label: "Unpaid",
          value: "unpaid"
        }
      ],
      classes: {
        container: "w-full mb-6",
      },
    },
    {
      name: "billType",
      label: "Bill Type",
      type: "select",
      placeholder: "All Types",
      required: false,
      options: billTypeOptions,
      classes: {
        container: "w-full mb-6",
      },
    },
    {
      name: "area",
      label: "Area",
      type: "multi-select",
      placeholder: "Select Area",
      required: false,
      options: areaList?.map((area: any) => ({
        value: area.id,
        label: area.name,
      })),
      className: "col-span-1",
      classes: {
        container: "w-full mb-6",
      },
    },
    {
      name: "sub_area",
      label: "Sub Area",
      type: "multi-select",
      placeholder: "Select Sub Area",
      required: false,
      options: subAreaListConfig,
      className: "col-span-1",
      classes: {},
    },
  ], [subAreaList]);

  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Handle field changes to update state without causing re-rendersa
  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "area") {
      setSelectedAreas(Array.isArray(value) ? value : []);
      // Clear sub_area selection when area changes
      setSelectedSubAreas([]);
      form.setValue("sub_area", []);
    } else if (fieldName === "sub_area") {
      setSelectedSubAreas(Array.isArray(value) ? value : []);
    }
  };

  const getStatusBadgeClass = (status: Consumer["status"]) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "PAUSE":
        return "bg-amber-100 text-amber-800";
      case "DISCONNECTED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-gray-800";
    }
  };

  // Define columns for the GenericTable
  const baseColumns: TableColumn[] = [
      { key: "remoteConsumerNumber", header: "Account No."},
      {
        key: "consumerInfo",
        header: "Name",
        render: (_, row) => (
          <div>
            <div className="font-medium">{row.firstName} {row.lastName}</div>
          </div>
        ),
      },
      { key: "premise", header: "Premise" },
      {
        key: "address",
        header: "Area/Subarea",
        render: (_, row) => (
          <div>
            <div>{row.area ?? "NA"}</div>
            <div className="text-sm text-gray-500">{row.subArea ?? "NA"}</div>
          </div>
        ),
      },
      { key: "createdDate", header: "Last Billed Date" },
      { key: "billAmount", header: "Billed Amount" },
      { key: "billStateLabel", header: "Last Bill Type" },
      { key: "paymentStatus", header: "Status" },
      {
        key: "actions",
        header: "Actions",
        render: (_, row) => (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => handleView(row)}
                >
                  <Eye size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View Consumer Profile</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
      },
    ];


  const handleView = (row: any) => {
    navigate(`/cx/consumer-management/${row.remoteConsumerNumber}`);
  };

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    setConsumerQueryParams({
      area: formValues.area || [],
      billType: formValues.billType || "",
      page: 1,
      sub_area: formValues.sub_area,
      status: formValues.status || "",
      export: false,
      searchData: search,
    });
  };


  const handleResetFilters = () => {
    form.reset();
    setSelectedAreas([]);
    setSelectedSubAreas([]);
     setConsumerQueryParams({
      area:  [],
      billType: "",
      page: 1,
      sub_area: [],
      status: "",
      export: false,
      searchData: search,
    });
  };

  const filters = (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onReset={handleResetFilters}
        onFieldChange={handleFieldChange}
        gridColumns={4}
        config={{
          layout: "vertical",
          showSubmitButton: false,
          showResetButton: false,
          classes: {
            form: "gap-4",
          },
        }}
        onSubmit={handleFilterSubmit}
      />
      <div className="flex justify-end gap-2 mt-4">
        <Button type="button" variant="outline" onClick={handleResetFilters}>
          Clear
        </Button>

        <Button type="button" onClick={handleFilterSubmit}>
          Apply Filters
        </Button>
      </div>
    </div>
  );

  const handleSearchChange = (value: string) => {
    setConsumerQueryParams({
      ...queryParams,
      searchData: value.length > 2 ? value : "",
      page: 1,
    });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="">
      <GenericTable
        data={consumerList?.result ?? []}
        columns={baseColumns}
        rowKey="id"
        filterPanel={filters}
        searchPlaceholder="Search by name or account..."
        onSearch={handleSearchChange}
        emptyMessage="No consumers available"
        onPageChange={(page) => handlePageChange(page)}
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil(consumerList?.count / PAGE_SIZE),
          totalItems: consumerList?.count,
          itemsPerPage: PAGE_SIZE,
          startItem: (currentPage - 1) * PAGE_SIZE + 1,
          endItem: Math.min(currentPage * PAGE_SIZE, consumerList?.count),
        }}
      />
    </div>
  );
};